import { SelectMenuItem } from 'bold-ui'
import { SelectDownshiftMenuProps } from 'bold-ui/lib/components/Select/SelectSingle/SelectDownshiftMenu'
import { CustomSelectFieldProps, SelectField } from 'components/form/final-form'
import { CiapSelectFieldDocument } from 'graphql/hooks.generated'
import { CiapQueryInput, CiapSelectFieldQuery, CiapSelectFieldQueryVariables } from 'graphql/types.generated'
import { useCallback, useMemo, useState } from 'react'
import { CypressProps } from 'types/cypress'
import { dedupById, emptyArrayOf, excludeIds } from 'util/array'

import { HeaderMeta } from '../model-motivoPrescricaoSelect'
import { RenderGrupoSelectHeader } from '../RenderGrupoSelectHeader'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'
import { buildItems, filterRegistradosByQuery } from '../utils-motivoPrescricaoSelect'
import { ciapItemToString, ciapRenderItem, CiapSelectFieldModel } from './CiapSelectField'

type MotivoPrescricaoCiapSelectFieldProps = CustomSelectFieldProps<CiapSelectFieldModel> &
  Pick<CiapQueryInput, 'sexo' | 'capitulosExcluidos' | 'excluirCIAPsDAB' | 'idadeCidadaoEmAnos'> &
  CypressProps & {
    itensRegistrados: ReadonlyArray<CiapSelectFieldModel>
  }

const ID_GRUPO_REGISTRADOS = 'grupoCiapsRegistrados'
const ID_GRUPO_OUTROS = 'grupoOutrosCiaps'

const makeHeaderItem = (id: string, meta: HeaderMeta): CiapSelectFieldModel =>
  ({ id, codigo: '', descricao: '', ...meta } as CiapSelectFieldModel & HeaderMeta)

const getCiapSearchableFields = (item: CiapSelectFieldModel): ReadonlyArray<string> => [item.descricao, item.codigo]

const MotivoPrescricaoCiapItem = (
  itemProps: SelectDownshiftMenuProps<CiapSelectFieldModel> & { item: CiapSelectFieldModel; index: number }
) => {
  const {
    index,
    downshift: { getItemProps, highlightedIndex },
    item,
    renderItem,
  } = itemProps

  if (item?.id === ID_GRUPO_REGISTRADOS || item?.id === ID_GRUPO_OUTROS) {
    const { headerTitulo, headerEmptyMessage } = item as CiapSelectFieldModel & HeaderMeta
    return (
      <RenderGrupoSelectHeader
        titulo={headerTitulo}
        emptyMessage={headerEmptyMessage}
        itemProps={getItemProps({ item, disabled: true })}
      />
    )
  }

  return (
    <SelectMenuItem selected={highlightedIndex === index} {...getItemProps({ item })}>
      {renderItem?.(item)}
    </SelectMenuItem>
  )
}

export function MotivoPrescricaoCiapSelectField(props: Readonly<MotivoPrescricaoCiapSelectFieldProps>) {
  const { sexo, capitulosExcluidos, excluirCIAPsDAB, idadeCidadaoEmAnos, itensRegistrados, dataTestId, ...rest } = props

  const [inputQuery, setInputQuery] = useState<string>('')

  const {
    skipping,
    selectProps: { items, onFilterChange: onFilterChangeBase, onFocus: onFocusBase, ...asyncPropsRest },
  } = useAsyncQuerySelect<CiapSelectFieldModel, CiapSelectFieldQuery, CiapSelectFieldQueryVariables>({
    query: CiapSelectFieldDocument,
    extractItems: (data) => data?.ciaps?.content?.filter((it): it is CiapSelectFieldModel => it != null),
    variables: (inputQuery: string): CiapSelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        pageParams: DEFAULT_SELECT_PAGE_PARAM,
        sexo,
        capitulosExcluidos,
        excluirCIAPsDAB,
        idadeCidadaoEmAnos,
      },
      fetchCID10Encaminhamento: false,
    }),
    skip: (inputString) => inputString?.trim()?.length < 2,
    debounceTime: 500,
  })

  const onFilterChange = useCallback<NonNullable<typeof onFilterChangeBase>>(
    (inputString, downshift) => {
      setInputQuery(inputString ?? '')
      onFilterChangeBase?.(inputString, downshift)
    },
    [onFilterChangeBase]
  )

  const onFocus = useCallback<NonNullable<typeof onFocusBase>>(
    (event) => {
      setInputQuery('')
      onFocusBase?.(event)
    },
    [onFocusBase]
  )

  const registrados = useMemo(() => itensRegistrados ?? emptyArrayOf<CiapSelectFieldModel>(), [itensRegistrados])

  const registradoIds = useMemo(() => new Set(registrados.map((it) => it.id)), [registrados])

  const filteredRegistrados = useMemo(
    () => filterRegistradosByQuery(registrados, inputQuery, getCiapSearchableFields),
    [registrados, inputQuery]
  )

  const menuItems = useMemo<CiapSelectFieldModel[]>(() => {
    const outros = excludeIds(dedupById(items ?? emptyArrayOf<CiapSelectFieldModel>()), registradoIds)
    const hasOutros = !skipping && outros.length > 0

    const headerRegistrados = makeHeaderItem(ID_GRUPO_REGISTRADOS, {
      headerTitulo: 'Registrados nesse atendimento',
      headerEmptyMessage: filteredRegistrados.length === 0 && 'Nenhum resultado encontrado',
    })

    const headerOutros = makeHeaderItem(ID_GRUPO_OUTROS, {
      headerTitulo: 'Outros',
      headerEmptyMessage: skipping ? 'Digite para buscar um CIAP 2.' : !hasOutros && 'Nenhum resultado encontrado',
    })

    return buildItems(headerRegistrados, filteredRegistrados, headerOutros, outros, !!skipping)
  }, [items, registradoIds, filteredRegistrados, skipping])

  return (
    <SelectField<CiapSelectFieldModel>
      itemToString={ciapItemToString}
      items={menuItems}
      renderItem={ciapRenderItem}
      components={{
        Item: MotivoPrescricaoCiapItem,
      }}
      dataTestId={dataTestId}
      onFilterChange={onFilterChange}
      onFocus={onFocus}
      {...asyncPropsRest}
      {...rest}
    />
  )
}
